<template>
  <div>
    <p class="has-text-weight-bold is-size-4">
      {{ $t("mp:new_application.page.title") }}
    </p>

    <div class="mp-card">
      <form @submit.prevent="save">
        <validation-observer ref="observer">
          <div class="is-flex is-flex-direction-column mb-3">
            <p class="has-text-weight-bold is-size-5">
              {{ $t("mp:new_application.sections.personal_data.title") }}
            </p>
            <div class="columns">
              <div class="column">
                <b-field
                  :label="$t('mp:new_application.field.other.last_name')"
                  label-position="on-border"
                >
                  <b-input v-model="application.customerLastName"></b-input>
                </b-field>
              </div>
              <div class="column">
                <b-field
                  :label="$t('mp:new_application.field.other.first_name')"
                  label-position="on-border"
                >
                  <b-input v-model="application.customerFirstName"></b-input>
                </b-field>
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <PhoneInput
                  :label="$t('mp:new_application.field.other.customer_phone')"
                  v-model="application.customerPhone"
                />
              </div>
              <div class="column">
                <BInputWithValidation
                  :label="$t('mp:new_application.field.other.customer_email')"
                  v-model="application.customerEmail"
                  label-position="on-border"
                  rules="required|email"
                />
              </div>
            </div>
            <p class="has-text-weight-bold is-size-5">
              {{ $t("mp:new_application.sections.order.title") }}
            </p>
            <div class="columns">
              <div class="column">
                <BInputWithValidation
                  :label="$t('mp:new_application.field.other.order_id')"
                  v-model="application.orderId"
                  label-position="on-border"
                  rules="required"
                />
              </div>
            </div>
            <p class="has-text-weight-bold is-size-5">
              {{ $t("mp:new_application.sections.items.title") }}
            </p>
            <ProductList />
            <p class="has-text-weight-bold is-size-5">
              {{ $t("mp:new_application.sections.calculation.title") }}
            </p>
            <b-field
              :label="$t('mp:new_application.field.offer')"
              label-position="on-border"
            >
              <b-select
                v-model="application.offerId"
                @input="offerSelected"
                expanded
              >
                <option
                  v-for="offer in offers"
                  :value="offer.offerId"
                  :key="offer.offerId"
                >
                  <template v-if="offer.downPayment === 0">
                    {{
                      `${offer.offerName} (${offer.installments} ${$t(
                        "mp:new_application.field.offer.installment"
                      )}, ${$t(
                        "mp:new_application.field.offer.no_down_payment"
                      )} ${$t("mp:new_application.field.offer.offer_id")}: ${
                        offer.offerId
                      })`
                    }}
                  </template>
                  <template v-else>
                    {{
                      `${offer.offerName} (${offer.installments} ${$t(
                        "mp:new_application.field.offer.installment"
                      )}, ${$t(
                        "mp:new_application.field.offer.down_payment"
                      )}: ${offer.downPayment}% ${$t(
                        "mp:new_application.field.offer.offer_id"
                      )}: ${offer.offerId})`
                    }}
                  </template>
                </option>
              </b-select>
            </b-field>
            <Calculation />
            <a
              class="my-6"
              @click="
                () => {
                  isOtherOpen = !isOtherOpen;
                }
              "
            >
              <template v-if="isOtherOpen">
                {{ $t("mp:new_application.button.hide_other_fields") }}
              </template>
              <template v-else>
                {{ $t("mp:new_application.button.show_other_fields") }}
              </template>
            </a>
            <div v-if="isOtherOpen" class="mx-4">
              <div class="columns">
                <div class="column">
                  <b-field
                    :label="$t('mp:new_application.field.other.checkout_id')"
                    label-position="on-border"
                  >
                    <b-input v-model="application.checkoutId"></b-input>
                  </b-field>
                </div>
                <div class="column">
                  <b-field
                    :label="$t('mp:new_application.field.other.customer_id')"
                    label-position="on-border"
                  >
                    <b-input v-model="application.customerId"></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <b-field
                    :label="$t('mp:new_application.field.other.invoice_id')"
                    label-position="on-border"
                  >
                    <b-input v-model="application.invoiceId"></b-input>
                  </b-field>
                </div>
                <div class="column">
                  <b-field
                    :label="
                      $t('mp:new_application.field.other.prepayment_invoice_id')
                    "
                    label-position="on-border"
                  >
                    <b-input
                      v-model="application.prepaymentInvoiceId"
                    ></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <b-field
                    :label="
                      $t('mp:new_application.field.other.final_invoice_id')
                    "
                    label-position="on-border"
                  >
                    <b-input v-model="application.finalInvoiceId"></b-input>
                  </b-field>
                </div>
                <div class="column">
                  <b-field
                    :label="$t('mp:new_application.field.other.redirect_url')"
                    label-position="on-border"
                  >
                    <b-input v-model="application.redirectUrl"></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column half-column">
                  <b-field
                    :label="$t('mp:new_application.field.other.financier')"
                    label-position="on-border"
                  >
                    <b-input v-model="application.financier"></b-input>
                  </b-field>
                </div>
              </div>
              <p>Billing address</p>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.billingAddress.country"
                    :label="$t('mp:new_application.field.address.country')"
                    label-position="on-border"
                    rules="length:2"
                  />
                </div>
                <div class="column">
                  <b-field
                    :label="$t('mp:new_application.field.address.city')"
                    label-position="on-border"
                  >
                    <b-input
                      v-model="application.billingAddress.city"
                    ></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.billingAddress.region"
                    :label="$t('mp:new_application.field.address.region')"
                    label-position="on-border"
                    rules="length:2"
                  />
                </div>
                <div class="column">
                  <b-field
                    :label="$t('mp:new_application.field.address.zip')"
                    label-position="on-border"
                  >
                    <b-input v-model="application.billingAddress.zip"></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.billingAddress.street"
                    :label="$t('mp:new_application.field.address.street')"
                    label-position="on-border"
                    rules="max:50"
                  />
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.billingAddress.street2"
                    :label="$t('mp:new_application.field.address.street2')"
                    label-position="on-border"
                    rules="max:50"
                  />
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.billingAddress.street3"
                    :label="$t('mp:new_application.field.address.street3')"
                    label-position="on-border"
                    rules="max:50"
                  />
                </div>
              </div>
              <p>Shipping address</p>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.shippingAddress.country"
                    :label="$t('mp:new_application.field.address.country')"
                    label-position="on-border"
                    rules="length:2"
                  />
                </div>
                <div class="column">
                  <b-field
                    :label="$t('mp:new_application.field.address.city')"
                    label-position="on-border"
                  >
                    <b-input
                      v-model="application.shippingAddress.city"
                    ></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.shippingAddress.region"
                    :label="$t('mp:new_application.field.address.region')"
                    label-position="on-border"
                    rules="length:2"
                  />
                </div>
                <div class="column">
                  <b-field
                    :label="$t('mp:new_application.field.address.zip')"
                    label-position="on-border"
                  >
                    <b-input
                      v-model="application.shippingAddress.zip"
                    ></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.shippingAddress.street"
                    :label="$t('mp:new_application.field.address.street')"
                    label-position="on-border"
                    rules="max:50"
                  />
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.shippingAddress.street2"
                    :label="$t('mp:new_application.field.address.street2')"
                    label-position="on-border"
                    rules="max:50"
                  />
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <BInputWithValidation
                    v-model.number="application.shippingAddress.street3"
                    :label="$t('mp:new_application.field.address.street3')"
                    label-position="on-border"
                    rules="max:50"
                  />
                </div>
              </div>
            </div>
          </div>
          <b-button native-type="submit" :loading="submitted" class="is-success"
            >{{ $t("mp:new_application.button.start") }}
          </b-button>
          <b-button @click="cancel" class="ml-4"
            >{{ $t("mp:new_application.button.cancel") }}
          </b-button>
        </validation-observer>
      </form>
    </div>
  </div>
</template>

<script>
import Calculation from "../components/Calculation.vue";
import ProductList from "../components/product-list/ProductList.vue";
import PhoneInput from "../components/input/PhoneInput.vue";
import BInputWithValidation from "../components/input/BInputWithValidation.vue";
import BnplApplicationSuccess from "../components/in-store-bnpl-modals/BnplApplicationSuccess.vue";
import BnplApplicationFail from "../components/in-store-bnpl-modals/BnplApplicationFail.vue";
import BnplApplicationCancel from "../components/in-store-bnpl-modals/BnplApplicationCancel.vue";

export default {
  name: "NewApplication",
  components: { ProductList, Calculation, PhoneInput, BInputWithValidation },
  title: "mp:page.new_bnpl_application.title",
  data() {
    return {
      isOtherOpen: false,
      submitted: false,
      offer: {},
      application: {
        offerId: null,
        orderId: null,
        shoppingMode: "IN_STORE",
        customerFirstName: null,
        customerLastName: null,
        customerPhone: null,
        customerEmail: null,
        financier: null,
        checkoutId: null,
        customerId: null,
        invoiceId: null,
        prepaymentInvoiceId: null,
        finalInvoiceId: null,
        redirectUrl: null,
        billingAddress: {
          country: "ZZ",
          city: null,
          region: null,
          zip: null,
          street: null,
          street2: null,
          street3: null,
        },
        shippingAddress: {
          country: "ZZ",
          city: null,
          region: null,
          zip: null,
          street: null,
          street2: null,
          street3: null,
        },
      },
    };
  },
  computed: {
    offers() {
      return this.$store.getters["offer/getOffers"];
    },
    applicationValue() {
      return this.$store.getters["bnplInStoreApplication/getApplicationValue"];
    },
  },
  methods: {
    async save() {
      this.submitted = true;
      const valid = await this.$refs.observer.validate();
      if (!valid) {
        this.submitted = false;
        return;
      }

      try {
        await this.$store.commit(
          "bnplInStoreApplication/saveApplication",
          this.application
        );
        await this.$store.dispatch("bnplInStoreApplication/create");
        this.submitted = false;
        this.$buefy.modal.open({
          parent: this,
          component: BnplApplicationSuccess,
          trapFocus: true,
          canCancel: ["escape", "outside"],
          width: 690,
        });
      } catch (ex) {
        this.$buefy.modal.open({
          parent: this,
          component: BnplApplicationFail,
          trapFocus: true,
          canCancel: ["escape", "outside"],
          width: 590,
        });
        this.submitted = false;
      }
    },
    async offerSelected() {
      this.offer = this.offers.find(
        (offer) => offer.offerId === this.application.offerId
      );
      this.$store.commit("offer/setOffer", this.application.offerId);
      this.$store.commit(
        "bnplInStoreApplication/setFinancierId",
        this.offer.financierIdentifier
      );
      await this.$store.dispatch("calculation/calculate");
    },
    cancel() {
      this.$buefy.modal.open({
        parent: this,
        component: BnplApplicationCancel,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        width: 590,
      });
    },
  },
  async beforeCreate() {
    await this.$store.dispatch("offer/fetch");
    this.application.offerId = this.$store.getters["offer/getOffer"];
    this.offer = this.offers.find(
      (offer) => offer.offerId === this.application.offerId
    );
    this.$store.commit(
      "bnplInStoreApplication/setFinancierId",
      this.offer.financierIdentifier
    );
  },
};
</script>

<style scoped lang="scss">
.half-column {
  flex: 0 0 50%;
}
</style>
